import DocumentAccess, { RefreshDocument } from './DocumentAccess'
import { DocumentAPI } from './API'
import {
  AccessCreatedMessage,
  AccessUpdatedMessage,
  AccessDeletedMessage,
} from './dtos/realtime-messages'
import {
  DocumentAccessDTO,
  UpdateDocumentAccessPayload,
  DocumentAccessPayload,
} from './dtos/document-access'
import { Id, PrimeProps } from 'types/definitions'
import { collection } from 'infra/collection'
import { PrimeMember, GetMember } from 'iam/MemberStore'
import { GetJob } from 'jobs/JobStore'
import { GetDocument } from './DocumentStore'

export type GetAccessesForDocument = (documentId: Id) => DocumentAccess[]

export type PrimeDocumentAccess = (
  props: PrimeProps<DocumentAccessDTO, 'id'>,
) => DocumentAccess | undefined

export default class DocumentAccessStore {
  accesses = collection<DocumentAccessDTO, DocumentAccess>({
    create: () =>
      new DocumentAccess(
        this.primeMember,
        this.getMember,
        this.getJob,
        this.getDocument,
        this.refreshDocument,
      ),
  })

  constructor(
    private api: DocumentAPI,
    private primeMember: PrimeMember,
    private getMember: GetMember,
    private getJob: GetJob,
    private getDocument: GetDocument,
    private refreshDocument: RefreshDocument,
  ) {
    this.onRemoteAccessCreate = this.onRemoteAccessCreate.bind(this)
    this.api.access
      .when<AccessCreatedMessage>('AccessCreated')
      .subscribe((e) => this.onRemoteAccessCreate(e.data))
    this.api.access
      .when<AccessUpdatedMessage>('AccessUpdated')
      .subscribe((e) => this.accesses.set(e.data))
    this.api.access
      .when<AccessDeletedMessage>('AccessDeleted')
      .subscribe((e) => this.accesses.remove(e.data.id))
  }

  forDocument: GetAccessesForDocument = (id) =>
    this.accesses.filter((a) => a.documentId === id)

  async onRemoteAccessCreate(data: DocumentAccessDTO) {
    const access: DocumentAccess = this.accesses.set(data)
    // When an AccessCreated event is pushed to us, we might not have the
    // latest version of the document in the store if we are not the owner
    // of the document.
    if (
      !access.document ||
      !access.document.owner ||
      !access.document.owner.isCurrentUser
    ) {
      await this.refreshDocument(access.documentId)
    }
  }

  async createAccess(documentId: string, data: DocumentAccessPayload) {
    return this.api.access
      .create({
        document_id: documentId,
        access: data,
      })
      .then((data) => this.accesses.set(data))
  }

  async updateAccess(accessId: string, data: UpdateDocumentAccessPayload) {
    return this.api.access
      .update(accessId, data)
      .then((data) => this.accesses.set(data))
  }

  async deleteAccess(accessId: string) {
    return this.api.access
      .delete(accessId)
      .then(() => this.accesses.remove(accessId))
  }
}
